import { getApiBaseUrl } from '../config/runtime';
import type {
  RemarketingJourneyListResponse,
  RemarketingJourneyStatus,
  RemarketingJourneyStep,
  RemarketingLeadListResponse,
  OverviewMetrics,
  FunnelResponse,
  CheckoutListResponse,
  CheckoutDetails,
  EventListResponse
} from '../types/api';
import type { MetricsParams, ListCheckoutsParams, ListEventsParams } from '../api/client';

// Cliente da área do ORGANIZADOR. Fala só com o BFF da própria origem
// (`/api/organizer/*`), com o Bearer recebido do Gestão e SEM cookies
// (`credentials: 'omit'`): a sessão administrativa, se existir no navegador,
// não participa. Não existe aqui o `remarketingApi` administrativo.
//
// O token vem da sessão a cada chamada e nunca vai para URL, storage ou log.

export class OrganizerApiError extends Error {
  /** 0 = sem resposta HTTP (rede). */
  readonly status: number;
  /** `error` do corpo JSON, quando houver. */
  readonly code: string | null;

  constructor(status: number, code: string | null) {
    super(describeOrganizerError(status, code));
    this.name = 'OrganizerApiError';
    this.status = status;
    this.code = code;
  }
}

export function describeOrganizerError(status: number, code: string | null): string {
  if (status === 401) return 'Sua sessão terminou. Recarregue a página do Gestão para continuar.';
  if (status === 403) {
    if (code === 'permission_denied') {
      return 'Sua conta não tem a permissão de gerenciar contatos, necessária para ver jornadas e leads.';
    }
    if (code === 'event_not_authorized') return 'Você não tem acesso a esse evento.';
    return 'Sua conta não tem acesso a estes dados.';
  }
  if (status === 422 && code === 'event_filter_required') {
    return 'Escolha um evento para consultar.';
  }
  if (status === 400) return 'Filtro inválido. Revise os campos e tente novamente.';
  if (status === 429) return 'Muitas consultas seguidas. Aguarde um instante e tente novamente.';
  return 'Remarketing indisponível no momento. Tente novamente em instantes.';
}

type Query = Record<string, string | number | null | undefined>;

async function getJson<T>(
  path: string,
  query: Query,
  token: string,
  signal: AbortSignal | undefined,
  fetchImpl: typeof fetch
): Promise<T> {
  const url = new URL(`${getApiBaseUrl()}/organizer${path}`);
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  let res: Response;
  try {
    res = await fetchImpl(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'omit',
      cache: 'no-store',
      signal
    });
  } catch (err) {
    // AbortError é fluxo normal (troca de filtro, desmontagem): segue cru.
    if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
    throw new OrganizerApiError(0, null);
  }

  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  if (!res.ok) {
    const code = (body as { error?: unknown } | null)?.error;
    throw new OrganizerApiError(res.status, typeof code === 'string' ? code : null);
  }
  // 200 sem JSON (página de borda, login de outro sistema) nunca vira dado.
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  if (!isJson || body === null || typeof body !== 'object') {
    throw new OrganizerApiError(502, 'invalid_response');
  }
  return body as T;
}

export interface RemarketingAccessResponse {
  user: { id: string; role: string };
  organizations: Array<{
    id: string;
    name: string | null;
    permissions: Record<string, boolean>;
    operations: string[];
    events: Array<{ id: string; title: string | null }>;
    eventsTruncated: boolean;
  }>;
}

/** `/access` com um token específico: a sessão confere a identidade antes de liberá-lo. */
export function fetchOrganizerAccess(
  token: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch
): Promise<RemarketingAccessResponse> {
  return getJson<RemarketingAccessResponse>('/access', {}, token, signal, fetchImpl);
}

export interface OrganizerJourneyParams {
  eventId?: string;
  step?: RemarketingJourneyStep;
  status?: RemarketingJourneyStatus;
  hasContact?: 'true' | 'false';
  activityFrom?: string;
  activityTo?: string;
  limit?: number;
  cursor?: string | null;
}

export interface OrganizerLeadParams {
  eventId?: string;
  source?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  limit?: number;
  cursor?: string | null;
}

/** O que a API precisa da sessão do organizador. */
export interface OrganizerTokenSource {
  /** Identidade atual; muda quando uma renovação traz outro usuário/organização. */
  readonly epoch: number;
  getAccessToken(options?: { forceRefresh?: boolean }): Promise<string>;
  /** 401 mesmo depois de renovar: a sessão acabou. */
  endSession(): void;
}

function staleIdentity(): Error {
  return new DOMException('organizer identity changed', 'AbortError');
}

export function createOrganizerDataApi(
  session: OrganizerTokenSource,
  fetchImpl: typeof fetch = fetch
) {
  async function authorizedGet<T>(path: string, query: Query, signal?: AbortSignal): Promise<T> {
    const epoch = session.epoch;

    const attempt = async (forceRefresh: boolean) => {
      const token = await session.getAccessToken(forceRefresh ? { forceRefresh: true } : undefined);
      if (session.epoch !== epoch) throw staleIdentity();
      const result = await getJson<T>(path, query, token, signal, fetchImpl);
      // Resposta que chegou depois da troca de identidade não é desta tela.
      if (session.epoch !== epoch) throw staleIdentity();
      return result;
    };

    try {
      return await attempt(false);
    } catch (err) {
      if (!(err instanceof OrganizerApiError) || err.status !== 401) throw err;
    }

    // Uma única renovação para o GET recusado com 401 — nunca em loop.
    try {
      return await attempt(true);
    } catch (err) {
      if (err instanceof OrganizerApiError && err.status === 401) session.endSession();
      throw err;
    }
  }

  return {
    tracking(organizationId: string, eventId?: string) {
      const base = `/organizations/${encodeURIComponent(organizationId)}/tracking`;
      return {
        getOverview(params: MetricsParams, signal?: AbortSignal): Promise<OverviewMetrics> {
          return authorizedGet(`${base}/metrics/overview`, { ...params, eventId }, signal);
        },
        getFunnel(params: MetricsParams, signal?: AbortSignal): Promise<FunnelResponse> {
          return authorizedGet(`${base}/metrics/funnel`, { ...params, eventId }, signal);
        },
        listCheckouts(
          params: ListCheckoutsParams,
          signal?: AbortSignal
        ): Promise<CheckoutListResponse> {
          return authorizedGet(`${base}/checkouts`, { ...params, eventId }, signal);
        },
        getCheckout(
          checkoutId: string,
          params: { limit?: number; source?: string; journeyId?: string } = {},
          signal?: AbortSignal
        ): Promise<CheckoutDetails> {
          return authorizedGet(
            `${base}/checkouts/${encodeURIComponent(checkoutId)}`,
            { ...params, eventId },
            signal
          );
        },
        listEvents(
          params: ListEventsParams & { journeyId?: string; sessionId?: string; source?: string },
          signal?: AbortSignal
        ): Promise<EventListResponse> {
          return authorizedGet(`${base}/events`, { ...params, eventId }, signal);
        }
      };
    },
    listJourneys(
      organizationId: string,
      params: OrganizerJourneyParams,
      signal?: AbortSignal
    ): Promise<RemarketingJourneyListResponse> {
      return authorizedGet(
        `/organizations/${encodeURIComponent(organizationId)}/journeys`,
        {
          eventId: params.eventId,
          step: params.step,
          status: params.status,
          hasContact: params.hasContact,
          activityFrom: params.activityFrom,
          activityTo: params.activityTo,
          limit: params.limit,
          cursor: params.cursor
        },
        signal
      );
    },

    listLeads(
      organizationId: string,
      params: OrganizerLeadParams,
      signal?: AbortSignal
    ): Promise<RemarketingLeadListResponse> {
      return authorizedGet(
        `/organizations/${encodeURIComponent(organizationId)}/leads`,
        {
          eventId: params.eventId,
          source: params.source,
          createdFrom: params.createdFrom,
          createdTo: params.createdTo,
          updatedFrom: params.updatedFrom,
          updatedTo: params.updatedTo,
          limit: params.limit,
          cursor: params.cursor
        },
        signal
      );
    }
  };
}

export type OrganizerDataApi = ReturnType<typeof createOrganizerDataApi>;
